import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import ViewNotes from "../../../../../../entities/DashboardPage/AccountingModule/Customer/Notes/ViewNotes";
import AuthService from "../../../../../../api/AuthService";
import LoaderHelper from "../../../../../../utils/Loader/LoaderHelper";
import { useAlert } from "../../../../../../context/AlertContext";

const ViewNotesPage = () => {
  const [loading, setLoading] = useState(false);
  const [editNoteData, setEditNoteData] = useState(null);
  const [invoicesList, setInvoicesList] = useState([]);
  const [bankData, setBankData] = useState([]);
  const { showAlert } = useAlert();
  const { state } = useLocation();
  const { item } = state || {};
  const { _id } = item || {};

  useEffect(() => {
    handleShowPdf();
    getAllBanks();
  }, []);

  const handleShowPdf = async () => {
    LoaderHelper.loaderStatus(true);
    const result = await AuthService.getNotesReceiptView(_id);
    LoaderHelper.loaderStatus(false);
    return { __html: result };
  };

  const handleDownloadReceipt = async () => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.handleDownloadNotesReceipt(_id);
      if (!result) {
        LoaderHelper.loaderStatus(false);
        showAlert(result?.message, "error");
      } else {
        const blob = new Blob([result], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `FileName.${"pdf"}`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);

        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  };

  const getEditNotesData = async (id) => {
    LoaderHelper.loaderStatus(true);
    setLoading(true)
    try {
      const result = await AuthService.getEditNotesData(id);
      setEditNoteData(result?.data);
      if (result?.success === true) {
        setLoading(false)
        LoaderHelper.loaderStatus(false);
      } else {
        setLoading(false)
        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      setLoading(false)
      LoaderHelper.loaderStatus(false);
    }
  };

  const getInvoiceList = async (id) => {
    LoaderHelper.loaderStatus(true);
    setLoading(true)
    try {
      const result = await AuthService.getInvoiceList(id);
      setInvoicesList(result?.data);
      if (result?.success === true) {
        setLoading(false)
        LoaderHelper.loaderStatus(false);
      } else {
        setLoading(false)
        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      setLoading(false)
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleApplyToInvoices = async ({ invoices, creditNoteId }) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.updateCreditNotes({ creditNoteId, payload: { invoices } });
      if (result?.success) {
        showAlert(result?.message, "success");
        LoaderHelper.loaderStatus(false);
        await getEditNotesData(_id);
      } else {
        showAlert(result?.message, "error");
        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
      showAlert("Failed to apply to invoices", "error");
    }
  }

  const getAllBanks = async () => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.handleGetAllCompanyBankAccount();
      if (result?.success) {
        setBankData(result?.data?.bankAccounts);
        LoaderHelper.loaderStatus(false);
      } else {
        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleRefundSubmit = async (id, payload, mode = 'create') => {
    LoaderHelper.loaderStatus(true);
    try {
      let result;
      if (mode === 'update') {
        result = await AuthService.updateRefundHistory(id, payload.transactionId, payload);
      } else {
        result = await AuthService.createRefund(id, payload);
      }

      if (result?.success) {
        showAlert(result?.message, "success");
        LoaderHelper.loaderStatus(false);
        getEditNotesData(_id);
      } else {
        showAlert(result?.message || "Refund failed", "error");
        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      showAlert(error?.response?.data?.message);
      LoaderHelper.loaderStatus(false);
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  const sendNotes = async () => {
    LoaderHelper.loaderStatus(true);
    setLoading(true)
    try {
      const result = await AuthService.sendNotes(_id);
      if (result?.success === true) {
        setLoading(false)
        showAlert(result?.message, "success");
      } else {
        setLoading(false)
        showAlert(result?.message, "error");
      }
    } catch (error) {
      setLoading(false)
      LoaderHelper.loaderStatus(false);
    }
  };

  const deleteCreditApply = async (id, creditAppliedID) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.deleteApplyCredit(id, creditAppliedID);
      if (result?.success === true) {
        LoaderHelper.loaderStatus(false);
        showAlert(result?.message, "success");
        getEditNotesData(_id);
      } else {
        LoaderHelper.loaderStatus(false);
        showAlert(result?.message, "error");
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  };

  const deleteRefundHistory = async (id, transactionId) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.deleteRefundHistory(id, transactionId);
      if (result?.success === true) {
        LoaderHelper.loaderStatus(false);
        showAlert(result?.message, "success");
        getEditNotesData(_id);
      } else {
        LoaderHelper.loaderStatus(false);
        showAlert(result?.message, "error");
      }
    } catch (error) {
      console.error("Delete Apply Credit Error:", error);
      LoaderHelper.loaderStatus(false);
    }
  }

  return (
    <ViewNotes handleDownloadReceipt={handleDownloadReceipt} handleShowPdf={handleShowPdf} getEditNotesData={getEditNotesData} editNoteData={editNoteData} loading={loading} getInvoiceList={getInvoiceList} invoicesList={invoicesList} handleApplyToInvoices={handleApplyToInvoices} bankData={bankData} handleRefundSubmit={handleRefundSubmit} handleSendNote={sendNotes} deleteCreditApply={deleteCreditApply} deleteRefundHistory={deleteRefundHistory} />
  )
}

export default ViewNotesPage;
